import { PremiumType, TimelineType } from "types/types";

export type AppSettingsType = {
  COUNTRY_CODE: string;
  CURRENCY_SYMBOL: string;
  BAR_SECTION_HEIGHT: number;
  PREMIUM_BAR_HEIGHT: number;
  POLICY_STATUS_BAR_HEIGHT: number;
  VEHICLE_STATUS_BAR_HEIGHT: number;
  LICENSE_BAR_HEIGHT: number;
  PERIOD_SELECTOR_HEIGHT: number;
  VEHICLE_TIMELINE_TYPES: TimelineType[];
  VEHICLE_PREMIUM_DOMAINS: PremiumType[];
  VEHICLE_PREMIUM_TYPES: PremiumType[];
  SUSPENSION_DAYS_INCLUDED: boolean;
  SUSPENSION_DAYS_DEFAULT: number | null;
  ACTIVATION_DAYS_INCLUDED: boolean;
  ACTIVATION_DAYS_DEFAULT: number | null;
  SUSPENSION_DAYS_LIMIT_INCLUDED: boolean;
  SUSPENSION_DAYS_LIMIT_DEFAULT: number | null;
  GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED: boolean;
  GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT: number | null;
  INSURER_SETTINGS_TEMPLATES: string[];
  INCLUDE_GROUP_POLICY: boolean;
  INCLUDE_INSURER_SETTING_PREMIUM_TYPE: boolean;
  INCLUDE_ISSUING_STATUS: boolean;
  INCLUDE_HULL_PRODUCT: boolean;
  INCLUDE_HULL_DEDUCTIBLE: boolean;
  INCLUDE_HULL_CALCULATION: boolean;
  INCLUDE_MACHINERY_BREAKDOWN_CALCULATION: boolean;
  INCLUDE_DEFAULT_SETTINGS: boolean;
  INCLUDE_INSURER_DEFAULT_SETTINGS: boolean;
  INCLUDE_UPDATE_FLEET_PREMIUM_FOR_ENGINE_TAX_20250401: boolean;
  INCLUDE_UPDATE_FLEET_PREMIUM_DOCUMENT_DATE: boolean;
  SHOW_REPORTS_BUTTON: boolean;
  SHOW_REPORTS_PRORATA_REPORT: boolean;
  SHOW_REPORTS_COMPARISON_REPORT: boolean;
  SHOW_REPORTS_COVERSHEET_REPORT: boolean;
  SHOW_REPORTS_FLEETLEGAL_REPORT: boolean;
  SHOW_REPORTS_HULL_ON_LAND_REPORT: boolean;
  SHOW_REPORTS_MACHINERY_BREAKDOWN_REPORT: boolean;
  SHOW_REPORTS_EXPORTFLEET_REPORT: boolean;
  SHOW_TRADE_LICENSE_PLATES: boolean;
  SHOW_SHARED_LICENSE_PLATES: boolean;
  SHOW_SCAN_DOCUMENTS: boolean;
  SHOW_HULL_PRODUCT: boolean;
  SHOW_LEASING_EXTRA_FIELDS: boolean;
  DEFAULT_COLUMNS: string[];
  HAS_GREENCARD_EXPIRY_DATE: boolean;
  HAS_SEARCH_BY_VIN: boolean;
  HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN: boolean;
  IS_VEHICLE_ACTIVATION_AVAILABLE: boolean;
  IS_VEHICLE_SUSPENSION_AVAILABLE: boolean;
  HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD: boolean;
  HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD: boolean;
  HAS_ANNUAL_PREMIUM_EUR: boolean;
  HAS_HULL_ADDITIONAL_COVER: boolean;
  HAS_ADDITIONAL_LEASING_FIELDS: boolean;
  HAS_LOAN_AMOUNT_FIELDS: boolean;
  HAS_SPECIAL_EQUIPMENT_LIST_FIELD: boolean;
  HAS_DUPLICATE_WITH_PREMIUMS_OPTION: boolean;
  HAS_TIMELINE_MENU_OPTIONS: boolean; // Add this new setting
  HAS_CLIENT_COMMENT_EXTERNAL_USER: boolean;
  HAS_SYNC_FLEET_INSURER_DATA: boolean; // New setting for syncing fleet insurer data
  INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS: boolean; // New setting for internal users changing vehicle status
  VISIBLE_FIELDS: string[]; // List of visible field names
  SHOW_BOOKINGS_WITH_DOCUMENT_DATE_CHECKBOX: boolean; // Optional setting for bookings with document date
  HAS_TEMP_VEHICLE_LIST: boolean;
  HAS_BOOKING_COMPARISON: boolean;
  SHOW_MAIN_INSURERS: boolean;
  SHOW_MAIN_INSURERS_LIST: boolean;
  SHOW_FLEET_EXCEL_UPLOAD: boolean;
  SHOW_FLEET_MOTOR_SPECIALIST: boolean;
  SHOW_DEFAULT_SETTINGS: boolean;
  SHOW_RECALCULATE_PRORATA_AND_BOOKINGS: boolean;
  HAS_MASS_ACTIONS: boolean;
  PAYMENT_METHODS: string[]; // List of payment method codes
  SHOW_URGENT_ON_STATUS_CHANGE: boolean;
  HAS_VEHICLE_HISTORY: boolean;
  HAS_VEHICLE_ATTACHMENTS: boolean; // Documents tab on the vehicle page (upload by internal users, clients see the ones flagged as visible)
  HAS_FLEET_ATTACHMENTS: boolean; // Documents section on the fleet details page (same rules as for vehicles)
  HAS_UPLOAD_VEHICLES: boolean; // "Upload Vehicles" (Excel import) on the fleet vehicle list (not offered in production)
  ALLOW_EXTERNAL_USER_EDIT_VEHICLE_FIELDS: boolean; // New setting for allowing external users to edit vehicle fields
};

const ALL_PAYMENT_METHODS = [
  "PaymentMethod.MONTHLY",
  "PaymentMethod.QUARTERLY",
  "PaymentMethod.SEMESTERLY",
  "PaymentMethod.ANNUALLY",
  "PaymentMethod.ONE_TIME",
  "PaymentMethod.INDIVIDUAL",
];
// Define all available fields
const ALL_VEHICLE_FIELDS = [
  // General form fields
  "licensePlate",
  "registrationDate",
  "vehicleOwner",
  "vehicleOwnerAddress",
  "vehicleOperator",
  "vehicleLastStatusDate",

  // Vehicle info fields
  "carUsageCode",
  "vin",
  "firstRegistrationDate",
  "yearOfConstruction",
  "nationalCodeId",
  "vehicleClassCode",
  "vehicleMainGroupCode",
  "vehicleTypeCode",
  "vehicleBodyTypeCode",
  "truckMountingCode",
  "brandCode",
  "model",
  "maxGrossWeightKg",
  "emptyWeightKg",
  "weightCapacityKg",
  "seats",
  "vehiclePowerTypeCode",
  "isPluginHybrid",
  "cubicCapacityCcm",
  "powerKw",
  "co2Emission",
  "certificateNumber",
  "carUsageCodeAdditional",
  "secondLicensePlate",
  "costCenterClient",
  "clientComment",
  "clientCoveragesInfo",
  "freeTextField1",
  "freeTextField2",

  // Finance general fields
  "comment",
  "vehicleIssuingStatusCode",
  "vehicleIssuingSubstatusCode",
  "vehicleIssuingComment",
  "leasingCompany",
  "leasingContractNumber",
  "leasingContractStartDate",
  "leasingContractEndDate",
  "leasingAmountPaidByLessee",
  "leasingAmountPaidByLesseeEur",
  "leasingCompanyAddress",
  "leasingCompanyTaxCode",
  "loanAmount",
  "loanAmountEur",
  "eurotaxCode",
  "bonusMalusCode",
  "listPrice",
  "listPriceEur",
  "listPriceTypeCode",
  "specialEquipment",
  "specialEquipmentList",
  "isIncludingTax",
  "vatReimbursment",
  "isLeasing",
  "machineryBreakdownListPrice",
  "machineryBreakdownListPriceTypeCode",
  "machineryBreakdownSpecialEquipment",
  "machineryBreakdownIsIncludingTax",

  // Premium fields (Finance temp fields)
  "mtplAnnualGrossPremium",
  "mtplAnnualGrossPremiumEur",
  "mtplAnnualNetPremium",
  "mtplAnnualNetPremiumEur",
  "hullAnnualGrossPremium",
  "hullAnnualGrossPremiumEur",
  "hullAnnualNetPremium",
  "hullAnnualNetPremiumEur",

  // Additional premium types that might exist
  "cargoAnnualGrossPremium",
  "cargoAnnualGrossPremiumEur",
  "cargoAnnualNetPremium",
  "cargoAnnualNetPremiumEur",
  "assistanceAnnualGrossPremium",
  "assistanceAnnualGrossPremiumEur",
  "assistanceAnnualNetPremium",
  "assistanceAnnualNetPremiumEur",
  "machineryBreakdownAnnualGrossPremium",
  "machineryBreakdownAnnualGrossPremiumEur",
  "machineryBreakdownAnnualNetPremium",
  "machineryBreakdownAnnualNetPremiumEur",
];

export enum InsurerSettingsTemplate {
  SUSPENDED_DAYS_LIMITS = "suspendedDaysLimits",
  GARAGE_RISK_DISCOUNT = "garageRiskDiscount",
  SUSPENSION_AND_ACTIVATION_DAYS = "suspensionAndActivationDays",
  INSURANCE_LINE_DETAILS = "insuranceLineDetails",
}
class AppSettings {
  private static instance: AppSettings;

  private constructor() {}

  // Method to get the singleton instance
  public static getInstance(): AppSettings {
    if (!AppSettings.instance) {
      AppSettings.instance = new AppSettings();
    }
    return AppSettings.instance;
  }

  public getAppSettingsTypes(): { [key: string]: AppSettingsType } {
    if (!AppSettings.instance) {
      AppSettings.instance = new AppSettings();
    }
    return AppSettings.appSettingsTypes;
  }

  private static hungarianSettings: AppSettingsType = {
    COUNTRY_CODE: "Country.HU",
    SHOW_FLEET_MOTOR_SPECIALIST: true,
    CURRENCY_SYMBOL: "Ft ",
    BAR_SECTION_HEIGHT: 114,
    PREMIUM_BAR_HEIGHT: 32,
    POLICY_STATUS_BAR_HEIGHT: 32,
    VEHICLE_STATUS_BAR_HEIGHT: 17.25,
    LICENSE_BAR_HEIGHT: 22,
    PERIOD_SELECTOR_HEIGHT: 57,
    VEHICLE_TIMELINE_TYPES: [
      TimelineType.VEHICLE_PREMIUM,
      TimelineType.VEHICLE_POLICY_STATUSES,
      TimelineType.VEHICLE_STATUS,
      TimelineType.VEHICLE_LICENSE_PLATE,
      TimelineType.SHARED_LICENSE_PLATE_STATUS,
    ],
    VEHICLE_PREMIUM_DOMAINS: [PremiumType.MTPL, PremiumType.HULL],
    VEHICLE_PREMIUM_TYPES: [
      PremiumType.MTPL,
      PremiumType.HULL,
      PremiumType.HULL_BASIC,
    ],
    SUSPENSION_DAYS_INCLUDED: false,
    SUSPENSION_DAYS_DEFAULT: 0,
    ACTIVATION_DAYS_INCLUDED: false,
    ACTIVATION_DAYS_DEFAULT: 0,
    SUSPENSION_DAYS_LIMIT_INCLUDED: false,
    SUSPENSION_DAYS_LIMIT_DEFAULT: 0,
    GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED: false,
    GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT: 0,
    INSURER_SETTINGS_TEMPLATES: [
      InsurerSettingsTemplate.INSURANCE_LINE_DETAILS,
      InsurerSettingsTemplate.GARAGE_RISK_DISCOUNT,
      InsurerSettingsTemplate.SUSPENSION_AND_ACTIVATION_DAYS,
    ],
    INCLUDE_GROUP_POLICY: true,
    INCLUDE_INSURER_SETTING_PREMIUM_TYPE: true,
    INCLUDE_ISSUING_STATUS: false,
    INCLUDE_HULL_PRODUCT: false,
    INCLUDE_HULL_DEDUCTIBLE: true,
    INCLUDE_HULL_CALCULATION: true,
    INCLUDE_MACHINERY_BREAKDOWN_CALCULATION: false,
    INCLUDE_DEFAULT_SETTINGS: true,
    INCLUDE_INSURER_DEFAULT_SETTINGS: true,
    INCLUDE_UPDATE_FLEET_PREMIUM_FOR_ENGINE_TAX_20250401: false,
    INCLUDE_UPDATE_FLEET_PREMIUM_DOCUMENT_DATE: true,
    SHOW_REPORTS_BUTTON: true,
    SHOW_REPORTS_PRORATA_REPORT: true,
    SHOW_REPORTS_COMPARISON_REPORT: true,
    SHOW_REPORTS_COVERSHEET_REPORT: true,
    SHOW_REPORTS_FLEETLEGAL_REPORT: false,
    SHOW_REPORTS_HULL_ON_LAND_REPORT: false,
    SHOW_REPORTS_MACHINERY_BREAKDOWN_REPORT: false,
    SHOW_REPORTS_EXPORTFLEET_REPORT: true,
    SHOW_TRADE_LICENSE_PLATES: false,
    SHOW_SHARED_LICENSE_PLATES: false,
    SHOW_SCAN_DOCUMENTS: false,
    SHOW_HULL_PRODUCT: false,
    SHOW_LEASING_EXTRA_FIELDS: true,

    DEFAULT_COLUMNS: [
      "vehicleStatusCode",
      "licensePlate",
      "fleetName",
      "options",
      "insuranceLineIcons",
      "mtplInsurerPartnerNumber",
      "mtplGroupPolicyNumber",
      "mtplPolicyNumber",
      "mtplIsPolicyIssued",
      "mtplPolicyDocumentDate",
      "mtplLastStartDate",
      "mtplImportEndDate",
      "mtplExpiryDate",
      "mtplGreenCardExpiryDate",
      "mtplPolicyStatus",
      "mtplPolicyStatusComment",
      "mtplPolicyStatusDateTime",
      "mtplAnnualGrossPremium",
      "mtplAnnualNetPremium",
      "mtplProRataGrossPremium",
      "mtplPaymentMethod",
      "mtplPaymentMean",
      "mtplComment",
      "hullInsurerPartnerNumber",
      "hullGroupPolicyNumber",
      "hullPolicyNumber",
      "hullIsPolicyIssued",
      "hullPolicyDocumentDate",
      "hullLastStartDate",
      "hullImportEndDate",
      "hullExpiryDate",
      "hullPolicyStatus",
      "hullPolicyStatusComment",
      "hullPolicyStatusDateTime",
      "hullAnnualGrossPremium",
      "hullAnnualNetPremium",
      "hullProRataGrossPremium",
      "hullPaymentMethod",
      "hullPaymentMean",

      "hullDeductibleComment",
      "hullDeductibleMaxAmount",
      "hullDeductibleMinAmount",
      "hullDeductiblePercent",
      "hullComment",
      "totalAnnualGrossPremium",
      "totalMtplRelatedAnnualGrossPremium",
      "totalProRataGrossPremium",
      "totalMtplRelatedProRataGrossPremium",
      "carUsageCode",
      "vehicleCreatedAt",
      "vehicleLastModifiedAt",
      "vehicleLastStatusDate",
      "vehicleLastStatusComment",
      "hullIsBasic",
      "vehicleClassCode",
      "needsGrECoUserAttention",
      "vin",
      // "vehicleMainGroupCode",
      "vehicleTypeCode",
      "truckMountingCode",
      "brandCode",
      "model",
      "costCenterClient",
      "leasingCompany",
      // "leasingContractNumber",
      // "leasingContractStartDate",
      // "leasingContractEndDate",
      // "leasingAmountPaidByLessee",
      // "eurotaxCode",
      // "bonusMalusCode",
      "clientComment",
      "seats",
      "powerKW",
      "cubicCapacityCcm",
      "weightCapacityKg",
      "maxGrossWeightKg",
      "emptyWeightKg",
      "yearOfConstruction",
      "firstRegistrationDate",
      "listPrice",
      "listPriceTypeCode",
      "specialEquipment",
      "secondLicensePlate",
      "vehicleOwner",
      "nationalCodeID",
      "co2Emission",
      "certificateNumber",
      "comment",
      "gap",
      "replacementValue",
      "grossNegligence",
      "electroPlusPackage",
      "isLeasing",
      "vatReimbursment",
      "vehiclePowerTypeCode",
      "isPluginHybrid",
      "freeTextField1",
      "freeTextField2",
      "hasAttachments",
    ],
    HAS_GREENCARD_EXPIRY_DATE: true,
    HAS_SEARCH_BY_VIN: false,
    HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN: false,
    IS_VEHICLE_ACTIVATION_AVAILABLE: true,
    IS_VEHICLE_SUSPENSION_AVAILABLE: true,
    HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD: true,
    HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD: true,
    HAS_ANNUAL_PREMIUM_EUR: true,
    HAS_HULL_ADDITIONAL_COVER: true,
    HAS_ADDITIONAL_LEASING_FIELDS: true,
    HAS_LOAN_AMOUNT_FIELDS: true,
    HAS_SPECIAL_EQUIPMENT_LIST_FIELD: true,
    HAS_DUPLICATE_WITH_PREMIUMS_OPTION: true,
    HAS_TIMELINE_MENU_OPTIONS: true,
    HAS_CLIENT_COMMENT_EXTERNAL_USER: false,
    HAS_SYNC_FLEET_INSURER_DATA: false,
    INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS: true,
    VISIBLE_FIELDS: ALL_VEHICLE_FIELDS.filter(
      (item) =>
        ![
          "vehicleMainGroupCode",
          "vehicleOwnerAddress",
          "vehicleOperator",
        ].includes(item),
    ),
    SHOW_BOOKINGS_WITH_DOCUMENT_DATE_CHECKBOX: true, // Optional setting for bookings with document date
    HAS_TEMP_VEHICLE_LIST: true,
    HAS_BOOKING_COMPARISON: false,
    SHOW_MAIN_INSURERS: true,
    SHOW_MAIN_INSURERS_LIST: true,
    SHOW_FLEET_EXCEL_UPLOAD: true,
    SHOW_DEFAULT_SETTINGS: true,
    SHOW_RECALCULATE_PRORATA_AND_BOOKINGS: true,
    HAS_MASS_ACTIONS: true,
    PAYMENT_METHODS: ALL_PAYMENT_METHODS.filter(
      (method) => !["PaymentMethod.INDIVIDUAL"].includes(method),
    ),
    SHOW_URGENT_ON_STATUS_CHANGE: false,
    HAS_VEHICLE_HISTORY: true,
    HAS_VEHICLE_ATTACHMENTS: true,
    HAS_FLEET_ATTACHMENTS: true,
    HAS_UPLOAD_VEHICLES: false,
    ALLOW_EXTERNAL_USER_EDIT_VEHICLE_FIELDS: true,
  };

  private static lithuanianSettings: AppSettingsType = {
    COUNTRY_CODE: "Country.LT",
    SHOW_FLEET_MOTOR_SPECIALIST: true,
    CURRENCY_SYMBOL: "€",
    BAR_SECTION_HEIGHT: 62,
    PREMIUM_BAR_HEIGHT: 32,
    POLICY_STATUS_BAR_HEIGHT: 32,
    VEHICLE_STATUS_BAR_HEIGHT: 17.25,
    LICENSE_BAR_HEIGHT: 22,
    PERIOD_SELECTOR_HEIGHT: 57,
    VEHICLE_TIMELINE_TYPES: [
      TimelineType.VEHICLE_PREMIUM,
      TimelineType.VEHICLE_PREMIUM_BOOKINGS,
      TimelineType.VEHICLE_STATUS,
      TimelineType.VEHICLE_LICENSE_PLATE,
      TimelineType.SHARED_LICENSE_PLATE_STATUS,
    ],
    VEHICLE_PREMIUM_DOMAINS: [PremiumType.MTPL, PremiumType.HULL],
    VEHICLE_PREMIUM_TYPES: [PremiumType.MTPL, PremiumType.HULL],
    SUSPENSION_DAYS_INCLUDED: false,
    SUSPENSION_DAYS_DEFAULT: 0,
    ACTIVATION_DAYS_INCLUDED: false,
    ACTIVATION_DAYS_DEFAULT: 0,
    SUSPENSION_DAYS_LIMIT_INCLUDED: false,
    SUSPENSION_DAYS_LIMIT_DEFAULT: 0,
    GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED: false,
    GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT: 0,
    INSURER_SETTINGS_TEMPLATES: [
      InsurerSettingsTemplate.INSURANCE_LINE_DETAILS,
      InsurerSettingsTemplate.GARAGE_RISK_DISCOUNT,
      InsurerSettingsTemplate.SUSPENSION_AND_ACTIVATION_DAYS,
    ],
    INCLUDE_GROUP_POLICY: false,
    INCLUDE_INSURER_SETTING_PREMIUM_TYPE: false,
    INCLUDE_ISSUING_STATUS: false,
    INCLUDE_HULL_PRODUCT: false,
    INCLUDE_HULL_DEDUCTIBLE: true,
    INCLUDE_HULL_CALCULATION: true,
    INCLUDE_MACHINERY_BREAKDOWN_CALCULATION: false,
    INCLUDE_DEFAULT_SETTINGS: false,
    INCLUDE_INSURER_DEFAULT_SETTINGS: false,
    INCLUDE_UPDATE_FLEET_PREMIUM_FOR_ENGINE_TAX_20250401: false,
    INCLUDE_UPDATE_FLEET_PREMIUM_DOCUMENT_DATE: true,
    SHOW_REPORTS_BUTTON: true,
    SHOW_REPORTS_PRORATA_REPORT: false,
    SHOW_REPORTS_COMPARISON_REPORT: false,
    SHOW_REPORTS_COVERSHEET_REPORT: false,
    SHOW_REPORTS_FLEETLEGAL_REPORT: false,
    SHOW_REPORTS_HULL_ON_LAND_REPORT: false,
    SHOW_REPORTS_MACHINERY_BREAKDOWN_REPORT: false,
    SHOW_REPORTS_EXPORTFLEET_REPORT: true,
    SHOW_TRADE_LICENSE_PLATES: false,
    SHOW_SHARED_LICENSE_PLATES: false,
    SHOW_SCAN_DOCUMENTS: false,
    SHOW_HULL_PRODUCT: false,
    SHOW_LEASING_EXTRA_FIELDS: true,

    DEFAULT_COLUMNS: [
      "vehicleStatusCode",
      "licensePlate",
      "fleetName",
      "options",
      "insuranceLineIcons",
      "mtplInsurerPartnerNumber",
      //"mtplGroupPolicyNumber",
      "mtplPolicyNumber",
      "mtplIsPolicyIssued",
      "mtplPolicyDocumentDate",
      "mtplLastStartDate",
      "mtplImportEndDate",
      "mtplExpiryDate",
      //"mtplGreenCardExpiryDate",
      //"mtplPolicyStatus",
      //"mtplPolicyStatusComment",
      //"mtplPolicyStatusDateTime",
      "mtplAnnualGrossPremium",
      "mtplAnnualNetPremium",
      "mtplProRataGrossPremium",
      "mtplPaymentMethod",
      "mtplPaymentMean",
      //"mtplComment",
      "hullInsurerPartnerNumber",
      //"hullGroupPolicyNumber",
      "hullPolicyNumber",
      "hullIsPolicyIssued",
      "hullPolicyDocumentDate",
      "hullLastStartDate",
      "hullImportEndDate",
      "hullExpiryDate",
      //"hullPolicyStatus",
      //"hullPolicyStatusComment",
      //"hullPolicyStatusDateTime",
      "hullAnnualGrossPremium",
      "hullAnnualNetPremium",
      "hullProRataGrossPremium",
      "hullPaymentMethod",
      "hullPaymentMean",
      //"hullComment",
      "totalAnnualGrossPremium",
      "totalMtplRelatedAnnualGrossPremium",
      "hullAnnualGrossPremium",
      "totalProRataGrossPremium",
      "totalMtplRelatedProRataGrossPremium",
      "hullProRataGrossPremium",
      // "totalBookingAmount",
      // "mtplRelatedBookingAmount",
      // "hullRelatedBookingAmount",
      // "carUsageCode",
      "vehicleCreatedAt",
      "vehicleLastModifiedAt",
      "vehicleLastStatusDate",
      "vehicleLastStatusComment",
      // "hullIsBasic",
      // "vehicleClassCode",
      //"needsGrECoUserAttention",
      "vin",
      // "vehicleMainGroupCode",
      "vehicleTypeCode",
      "brandCode",
      "model",
      // "costCenterClient",
      // "leasingCompany",
      // "leasingContractNumber",
      // "leasingContractStartDate",
      // "leasingContractEndDate",
      // "leasingAmountPaidByLessee",
      // "eurotaxCode",
      // "bonusMalusCode",
      "truckMountingCode",

      "clientComment",
      "clientCoveragesInfo",
      // "seats",
      "powerKW",
      "cubicCapacityCcm",
      // "weightCapacityKg",
      // "maxGrossWeightKg",
      // "emptyWeightKg",
      "yearOfConstruction",
      // "firstRegistrationDate",
      "listPrice",
      //"listPriceTypeCode",
      // "specialEquipment",
      // "secondLicensePlate",
      "vehicleOwner",
      "vehicleOperator",
      // "nationalCodeID",
      // "co2Emission",
      // "certificateNumber",
      "comment",
      // "gap",
      // "replacementValue",
      // "grossNegligence",
      // "electroPlusPackage",
      "isLeasing",
      // "vatReimbursment",
      // "vehiclePowerTypeCode",
      // "isPluginHybrid",
      "freeTextField1",
      "freeTextField2",
      "hasAttachments",
    ],
    HAS_GREENCARD_EXPIRY_DATE: true,
    HAS_SEARCH_BY_VIN: false,
    HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN: true,
    IS_VEHICLE_ACTIVATION_AVAILABLE: false,
    IS_VEHICLE_SUSPENSION_AVAILABLE: false,
    HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD: false,
    HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD: false,
    HAS_ANNUAL_PREMIUM_EUR: false,
    HAS_HULL_ADDITIONAL_COVER: false,
    HAS_ADDITIONAL_LEASING_FIELDS: false,
    HAS_LOAN_AMOUNT_FIELDS: false,
    HAS_SPECIAL_EQUIPMENT_LIST_FIELD: false,
    HAS_DUPLICATE_WITH_PREMIUMS_OPTION: false,
    HAS_TIMELINE_MENU_OPTIONS: false, // Only Lithuania has this disabled
    HAS_CLIENT_COMMENT_EXTERNAL_USER: true,
    HAS_SYNC_FLEET_INSURER_DATA: true,
    INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS: false,
    VISIBLE_FIELDS: ALL_VEHICLE_FIELDS.filter(
      (item) =>
        ![
          "carUsageCode",
          "registrationDate",
          "firstRegistrationDate",
          "nationalCodeId",
          "vehicleClassCode",
          "vehicleMainGroupCode",
          "vehicleBodyTypeCode",
          "truckMountingCode",
          "emptyWeightKg",
          "weightCapacityKg",
          "seats",
          "vehiclePowerTypeCode",
          "isPluginHybrid",
          "co2Emission",
          "certificateNumber",
          "carUsageCodeAdditional",
          "secondLicensePlate",
          "costCenterClient",

          // Finance general fields
          "vehicleIssuingStatusCode",
          "vehicleIssuingSubstatusCode",
          "vehicleIssuingComment",
          "leasingCompany",
          "leasingContractNumber",
          "leasingContractStartDate",
          "leasingContractEndDate",
          "leasingAmountPaidByLessee",
          "leasingAmountPaidByLesseeEur",
          "leasingCompanyAddress",
          "leasingCompanyTaxCode",
          "loanAmount",
          "loanAmountEur",
          "eurotaxCode",
          "bonusMalusCode",
          "listPriceEur",
          "listPriceTypeCode",
          "specialEquipment",
          "specialEquipmentList",
          "isIncludingTax",
          "vatReimbursment",
          "machineryBreakdownListPrice",
          "machineryBreakdownListPriceTypeCode",
          "machineryBreakdownSpecialEquipment",
          "machineryBreakdownIsIncludingTax",

          // Premium fields (Finance temp fields)
          "mtplAnnualGrossPremium",
          "mtplAnnualGrossPremiumEur",
          "mtplAnnualNetPremium",
          "mtplAnnualNetPremiumEur",
          "hullAnnualGrossPremium",
          "hullAnnualGrossPremiumEur",
          "hullAnnualNetPremium",
          "hullAnnualNetPremiumEur",

          // Additional premium types that might exist
          "cargoAnnualGrossPremium",
          "cargoAnnualGrossPremiumEur",
          "cargoAnnualNetPremium",
          "cargoAnnualNetPremiumEur",
          "assistanceAnnualGrossPremium",
          "assistanceAnnualGrossPremiumEur",
          "assistanceAnnualNetPremium",
          "assistanceAnnualNetPremiumEur",
          "machineryBreakdownAnnualGrossPremium",
          "machineryBreakdownAnnualGrossPremiumEur",
          "machineryBreakdownAnnualNetPremium",
          "machineryBreakdownAnnualNetPremiumEur",
        ].includes(item),
    ),
    SHOW_BOOKINGS_WITH_DOCUMENT_DATE_CHECKBOX: false,
    HAS_TEMP_VEHICLE_LIST: false,
    HAS_BOOKING_COMPARISON: false,
    SHOW_MAIN_INSURERS: true,
    SHOW_MAIN_INSURERS_LIST: false,
    SHOW_FLEET_EXCEL_UPLOAD: false,
    SHOW_DEFAULT_SETTINGS: false,
    SHOW_RECALCULATE_PRORATA_AND_BOOKINGS: false,
    HAS_MASS_ACTIONS: false,
    PAYMENT_METHODS: ALL_PAYMENT_METHODS,
    SHOW_URGENT_ON_STATUS_CHANGE: true,
    HAS_VEHICLE_HISTORY: true,
    HAS_VEHICLE_ATTACHMENTS: true,
    HAS_FLEET_ATTACHMENTS: true,
    HAS_UPLOAD_VEHICLES: true,
    ALLOW_EXTERNAL_USER_EDIT_VEHICLE_FIELDS: true,
  };

  private static czechSettings: AppSettingsType = {
    COUNTRY_CODE: "Country.CZ",
    SHOW_FLEET_MOTOR_SPECIALIST: true,
    CURRENCY_SYMBOL: "Kč ",
    BAR_SECTION_HEIGHT: 62,
    PREMIUM_BAR_HEIGHT: 32,
    POLICY_STATUS_BAR_HEIGHT: 32,
    VEHICLE_STATUS_BAR_HEIGHT: 17.25,
    LICENSE_BAR_HEIGHT: 22,
    PERIOD_SELECTOR_HEIGHT: 57,
    VEHICLE_TIMELINE_TYPES: [
      TimelineType.VEHICLE_PREMIUM,
      // TimelineType.VEHICLE_PREMIUM_BOOKINGS, // CZ has no bookings (as HU)
      TimelineType.VEHICLE_STATUS,
      TimelineType.VEHICLE_LICENSE_PLATE,
      TimelineType.SHARED_LICENSE_PLATE_STATUS,
    ],
    VEHICLE_PREMIUM_DOMAINS: [
      PremiumType.MTPL,
      PremiumType.ACCIDENT,
      PremiumType.ASSISTANCE,
      PremiumType.LEGAL,
      PremiumType.HULL,
    ],
    VEHICLE_PREMIUM_TYPES: [
      PremiumType.MTPL,
      PremiumType.ACCIDENT,
      PremiumType.ASSISTANCE,
      PremiumType.LEGAL,
      PremiumType.HULL,
      PremiumType.HULL_BASIC,
    ],
    SUSPENSION_DAYS_INCLUDED: false,
    SUSPENSION_DAYS_DEFAULT: 0,
    ACTIVATION_DAYS_INCLUDED: false,
    ACTIVATION_DAYS_DEFAULT: 0,
    SUSPENSION_DAYS_LIMIT_INCLUDED: false,
    SUSPENSION_DAYS_LIMIT_DEFAULT: 0,
    GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED: false,
    GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT: 0,
    INSURER_SETTINGS_TEMPLATES: [
      InsurerSettingsTemplate.SUSPENDED_DAYS_LIMITS,
      InsurerSettingsTemplate.GARAGE_RISK_DISCOUNT,
      InsurerSettingsTemplate.SUSPENSION_AND_ACTIVATION_DAYS,
    ],
    INCLUDE_GROUP_POLICY: false,
    INCLUDE_INSURER_SETTING_PREMIUM_TYPE: false,
    INCLUDE_ISSUING_STATUS: false,
    INCLUDE_HULL_PRODUCT: false,
    INCLUDE_HULL_DEDUCTIBLE: true,
    INCLUDE_HULL_CALCULATION: true,
    INCLUDE_MACHINERY_BREAKDOWN_CALCULATION: false,
    INCLUDE_DEFAULT_SETTINGS: true,
    INCLUDE_INSURER_DEFAULT_SETTINGS: true,
    INCLUDE_UPDATE_FLEET_PREMIUM_FOR_ENGINE_TAX_20250401: false,
    INCLUDE_UPDATE_FLEET_PREMIUM_DOCUMENT_DATE: true,
    SHOW_REPORTS_BUTTON: true,
    SHOW_REPORTS_PRORATA_REPORT: false,
    SHOW_REPORTS_COMPARISON_REPORT: false,
    SHOW_REPORTS_COVERSHEET_REPORT: false,
    SHOW_REPORTS_FLEETLEGAL_REPORT: false,
    SHOW_REPORTS_HULL_ON_LAND_REPORT: false,
    SHOW_REPORTS_MACHINERY_BREAKDOWN_REPORT: false,
    SHOW_REPORTS_EXPORTFLEET_REPORT: true,
    SHOW_TRADE_LICENSE_PLATES: true,
    SHOW_SHARED_LICENSE_PLATES: false,
    SHOW_SCAN_DOCUMENTS: false,
    SHOW_HULL_PRODUCT: false,
    SHOW_LEASING_EXTRA_FIELDS: true,

    DEFAULT_COLUMNS: [
      "vehicleStatusCode",
      "licensePlate",
      "fleetName",
      "options",
      "insuranceLineIcons",
      "mtplInsurerPartnerNumber",
      //"mtplGroupPolicyNumber",
      "mtplPolicyNumber",
      "mtplIsPolicyIssued",
      "mtplPolicyDocumentDate",
      "mtplLastStartDate",
      "mtplImportEndDate",
      //"mtplExpiryDate", // CZ: hidden (set by the backend 30 years ahead)
      //"mtplGreenCardExpiryDate",
      //"mtplPolicyStatus",
      //"mtplPolicyStatusComment",
      //"mtplPolicyStatusDateTime",
      "mtplAnnualGrossPremium",
      "mtplAnnualNetPremium",
      "mtplProRataGrossPremium",
      "mtplPaymentMethod",
      "mtplPaymentMean",
      "mtplComment",
      "hullInsurerPartnerNumber",
      //"hullGroupPolicyNumber",
      "hullPolicyNumber",
      "hullIsPolicyIssued",
      "hullPolicyDocumentDate",
      "hullLastStartDate",
      "hullImportEndDate",
      //"hullExpiryDate", // CZ: hidden (set by the backend 30 years ahead)
      //"hullPolicyStatus",
      //"hullPolicyStatusComment",
      //"hullPolicyStatusDateTime",
      "hullAnnualGrossPremium",
      "hullAnnualNetPremium",
      "hullProRataGrossPremium",
      "hullPaymentMethod",
      "hullPaymentMean",
      "hullComment",
      "totalAnnualGrossPremium",
      "totalMtplRelatedAnnualGrossPremium",
      "totalProRataGrossPremium",
      "totalMtplRelatedProRataGrossPremium",
      "carUsageCode",
      "truckMountingCode",
      "vehicleCreatedAt",
      "vehicleLastModifiedAt",
      "vehicleLastStatusDate",
      "vehicleLastStatusComment",
      "hullIsBasic",
      "vehicleClassCode",
      "needsGrECoUserAttention",
      "vin",
      // "vehicleMainGroupCode",
      "vehicleTypeCode",
      "brandCode",
      "model",
      "costCenterClient",
      "leasingCompany",

      "clientComment",
      "clientCoveragesInfo",
      "seats",
      "powerKW",
      "cubicCapacityCcm",
      "weightCapacityKg",
      "maxGrossWeightKg",
      "emptyWeightKg",
      "yearOfConstruction",
      "firstRegistrationDate",
      "listPrice",
      "listPriceTypeCode",
      "specialEquipment",
      "secondLicensePlate",
      "vehicleOwner",
      "vehicleOperator",
      "nationalCodeID",
      "co2Emission",
      "certificateNumber",
      "comment",
      "gap",
      "replacementValue",
      "grossNegligence",
      "electroPlusPackage",
      "isLeasing",
      "vatReimbursment",
      "vehiclePowerTypeCode",
      "isPluginHybrid",
      "freeTextField1",
      "freeTextField2",
      "hasAttachments",
    ],
    HAS_GREENCARD_EXPIRY_DATE: false,
    HAS_SEARCH_BY_VIN: true,
    HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN: false,
    IS_VEHICLE_ACTIVATION_AVAILABLE: true,
    IS_VEHICLE_SUSPENSION_AVAILABLE: false,
    HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD: false,
    HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD: false,
    HAS_ANNUAL_PREMIUM_EUR: false,
    HAS_HULL_ADDITIONAL_COVER: false,
    HAS_ADDITIONAL_LEASING_FIELDS: false,
    HAS_LOAN_AMOUNT_FIELDS: false,
    HAS_SPECIAL_EQUIPMENT_LIST_FIELD: false,
    HAS_DUPLICATE_WITH_PREMIUMS_OPTION: true,
    HAS_TIMELINE_MENU_OPTIONS: true,
    HAS_CLIENT_COMMENT_EXTERNAL_USER: false,
    HAS_SYNC_FLEET_INSURER_DATA: false,
    INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS: true,

    VISIBLE_FIELDS: ALL_VEHICLE_FIELDS.filter(
      (item) => !["vehicleMainGroupCode"].includes(item),
    ),
    SHOW_BOOKINGS_WITH_DOCUMENT_DATE_CHECKBOX: true,
    HAS_TEMP_VEHICLE_LIST: true,
    HAS_BOOKING_COMPARISON: false,
    SHOW_MAIN_INSURERS: true,
    SHOW_MAIN_INSURERS_LIST: true,
    SHOW_FLEET_EXCEL_UPLOAD: true,
    SHOW_DEFAULT_SETTINGS: true,
    SHOW_RECALCULATE_PRORATA_AND_BOOKINGS: true,
    HAS_MASS_ACTIONS: true,
    PAYMENT_METHODS: ALL_PAYMENT_METHODS.filter(
      (method) => !["PaymentMethod.INDIVIDUAL"].includes(method),
    ),
    SHOW_URGENT_ON_STATUS_CHANGE: false,
    HAS_VEHICLE_HISTORY: true,
    HAS_VEHICLE_ATTACHMENTS: true,
    HAS_FLEET_ATTACHMENTS: true,
    HAS_UPLOAD_VEHICLES: true,
    ALLOW_EXTERNAL_USER_EDIT_VEHICLE_FIELDS: true,
  };

  private static austrianSettings: AppSettingsType = {
    COUNTRY_CODE: "Country.AT",
    SHOW_FLEET_MOTOR_SPECIALIST: true,
    CURRENCY_SYMBOL: "€",
    BAR_SECTION_HEIGHT: 62,
    PREMIUM_BAR_HEIGHT: 32,
    POLICY_STATUS_BAR_HEIGHT: 32,
    VEHICLE_STATUS_BAR_HEIGHT: 17.25,
    LICENSE_BAR_HEIGHT: 22,
    PERIOD_SELECTOR_HEIGHT: 57,
    VEHICLE_TIMELINE_TYPES: [
      TimelineType.VEHICLE_PREMIUM,
      TimelineType.VEHICLE_PREMIUM_BOOKINGS,
      TimelineType.VEHICLE_STATUS,
      TimelineType.VEHICLE_LICENSE_PLATE,
      TimelineType.SHARED_LICENSE_PLATE_STATUS,
    ],
    VEHICLE_PREMIUM_DOMAINS: [
      PremiumType.MTPL,
      PremiumType.HORSE_POWER,
      PremiumType.ACCIDENT,
      PremiumType.ASSISTANCE,
      PremiumType.LEGAL,
      PremiumType.HULL,
      PremiumType.FLEETLEGAL_PRORATA,
      ...(process.env.REACT_APP_ENVIRONMENT !== "P"
        ? [
            PremiumType.HULL_ON_LAND_PRORATA,
            PremiumType.MACHINERY_BREAKDOWN_PRORATA,
          ]
        : []),
    ],
    VEHICLE_PREMIUM_TYPES: [
      PremiumType.MTPL,
      PremiumType.HORSE_POWER,
      PremiumType.ACCIDENT,
      PremiumType.ASSISTANCE,
      PremiumType.LEGAL,
      PremiumType.HULL,
      PremiumType.HULL_BASIC,
      PremiumType.FLEETLEGAL_PRORATA,
      PremiumType.FLEETLEGAL_CLOSINGDATE,
      ...(process.env.REACT_APP_ENVIRONMENT !== "P"
        ? [
            PremiumType.HULL_ON_LAND_PRORATA,
            PremiumType.HULL_ON_LAND_CLOSINGDATE,
            PremiumType.MACHINERY_BREAKDOWN_PRORATA,
            PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE,
          ]
        : []),
    ],
    SUSPENSION_DAYS_INCLUDED: true,
    SUSPENSION_DAYS_DEFAULT: null,
    ACTIVATION_DAYS_INCLUDED: true,
    ACTIVATION_DAYS_DEFAULT: null,
    SUSPENSION_DAYS_LIMIT_INCLUDED: true,
    SUSPENSION_DAYS_LIMIT_DEFAULT: null,
    GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED: true,
    GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT: null,
    INSURER_SETTINGS_TEMPLATES: [
      InsurerSettingsTemplate.SUSPENDED_DAYS_LIMITS,
      InsurerSettingsTemplate.GARAGE_RISK_DISCOUNT,
      InsurerSettingsTemplate.SUSPENSION_AND_ACTIVATION_DAYS,
    ],
    INCLUDE_GROUP_POLICY: false,
    INCLUDE_INSURER_SETTING_PREMIUM_TYPE: false,
    INCLUDE_ISSUING_STATUS: true,
    INCLUDE_HULL_PRODUCT: true,
    INCLUDE_HULL_DEDUCTIBLE: true,
    INCLUDE_HULL_CALCULATION: true,
    INCLUDE_MACHINERY_BREAKDOWN_CALCULATION: true,
    INCLUDE_DEFAULT_SETTINGS: true,
    INCLUDE_INSURER_DEFAULT_SETTINGS: true,
    INCLUDE_UPDATE_FLEET_PREMIUM_FOR_ENGINE_TAX_20250401: true,
    INCLUDE_UPDATE_FLEET_PREMIUM_DOCUMENT_DATE: true,
    SHOW_REPORTS_BUTTON: true,
    SHOW_REPORTS_PRORATA_REPORT: true,
    SHOW_REPORTS_COMPARISON_REPORT: true,
    SHOW_REPORTS_COVERSHEET_REPORT: true,
    SHOW_REPORTS_FLEETLEGAL_REPORT: true,
    SHOW_REPORTS_HULL_ON_LAND_REPORT: true,
    SHOW_REPORTS_MACHINERY_BREAKDOWN_REPORT: true,
    SHOW_REPORTS_EXPORTFLEET_REPORT: true,
    SHOW_TRADE_LICENSE_PLATES: true,
    SHOW_SHARED_LICENSE_PLATES: true,
    SHOW_SCAN_DOCUMENTS: true,
    SHOW_HULL_PRODUCT: true,
    SHOW_LEASING_EXTRA_FIELDS: false,

    DEFAULT_COLUMNS: [
      "vehicleStatusCode",
      "licensePlate",
      "fleetName",
      "options",
      "insuranceLineIcons",
      "vehicleIssuingStatusCode",
      "totalAnnualGrossPremium",
      "bookingAmountTotal",
      "totalMtplRelatedAnnualGrossPremium",
      "totalProRataGrossPremium",
      "totalMtplRelatedProRataGrossPremium",
      "totalBookingAmount",
      "mtplRelatedBookingAmount",
      "hullRelatedBookingAmount",
      "mtplInsurerPartnerNumber",
      //"mtplGroupPolicyNumber",
      "mtplPolicyNumber",
      "mtplIsPolicyIssued",
      "mtplPolicyDocumentDate",
      "mtplLastStartDate",
      "mtplImportEndDate",
      "accidentLastStartDate",
      "assistanceLastStartDate",
      "legalLastStartDate",
      "horsePowerBasedTaxLastStartDate",
      "mtplExpiryDate",
      //"mtplGreenCardExpiryDate",
      //"mtplPolicyStatus",
      //"mtplPolicyStatusComment",
      //"mtplPolicyStatusDateTime",
      "mtplAnnualGrossPremium",
      "bookingAmountMtpl",
      "accidentAnnualGrossPremium",
      "bookingAmountAccident",
      "assistanceAnnualGrossPremium",
      "bookingAmountAssistance",
      "legalAnnualGrossPremium",
      "bookingAmountLegal",
      "horsePowerBasedTaxAnnualy",
      "bookingAmountEngineTax",
      "mtplAnnualNetPremium",
      "mtplProRataGrossPremium",
      "accidentProRataGrossPremium",
      "assistanceProRataGrossPremium",
      "legalProRataGrossPremium",
      "horsePowerBasedTaxProRata",
      "horsePowerBasedTaxPaymentMethod",
      "mtplPaymentMethod",
      "mtplPaymentMean",
      "mtplComment",
      "accidentComment",
      "assistanceComment",
      "legalComment",
      "horsePowerBasedTaxComment",
      "hullInsurerPartnerNumber",
      //"hullGroupPolicyNumber",
      "hullPolicyNumber",
      "hullIsPolicyIssued",
      "hullPolicyDocumentDate",
      "hullLastStartDate",
      "hullImportEndDate",
      "hullExpiryDate",
      //"hullPolicyStatus",
      //"hullPolicyStatusComment",
      //"hullPolicyStatusDateTime",
      "hullAnnualGrossPremium",
      "bookingAmountHull",
      "hullAnnualNetPremium",
      "hullProRataGrossPremium",
      "hullPaymentMethod",
      "hullPaymentMean",
      "hullDeductibleComment",
      "hullDeductibleMaxAmount",
      "hullDeductibleMinAmount",
      "hullDeductiblePercent",
      "hullComment",
      "hullIsBasic",
      "hullProduct",
      "carUsageCode",
      "vehicleCreatedAt",
      "vehicleLastModifiedAt",
      "vehicleLastStatusDate",
      "vehicleLastStatusComment",
      "hasSharedLicensePlate",
      "hadSharedLicensePlate",
      "hasIndemnityPremium",
      "hasBonusMalusPremium",
      "hadIndemnityPremium",
      "hadBonusMalusPremium",
      "vehicleClassCode",
      "needsGrECoUserAttention",
      "vin",
      "vehicleMainGroupCode",
      "vehicleTypeCode",
      "brandCode",
      "model",
      "costCenterClient",
      "leasingCompany",
      "clientComment",
      "clientCoveragesInfo",
      "seats",
      "powerKW",
      "cubicCapacityCcm",
      "weightCapacityKg",
      "maxGrossWeightKg",
      "emptyWeightKg",
      "yearOfConstruction",
      "firstRegistrationDate",
      "listPrice",
      "listPriceTypeCode",
      "truckMountingCode",
      "specialEquipment",
      "secondLicensePlate",
      "vehicleOwner",
      "vehicleOperator",
      "nationalCodeID",
      "co2Emission",
      "certificateNumber",
      "comment",
      "excludeHorsePowerBasedTaxReason",
      "excludeHorsePowerBasedTax",
      "gap",
      "replacementValue",
      "grossNegligence",
      "electroPlusPackage",
      "isIncludingTax",
      "isLeasing",
      "vatReimbursment",
      "vehiclePowerTypeCode",
      "isPluginHybrid",
      "vehicleIssuingComment",
      "vehicleIssuingSubstatusCode",
      "fleetLegalPolicyNumber",
      "fleetLegalIsPolicyIssued",
      "fleetLegalPolicyDocumentDate",
      "fleetLegalInsurerPartnerNumber",
      "fleetLegalAnnualGrossPremium",
      "bookingAmountFleetLegal",
      "fleetLegalProRataGrossPremium",
      "fleetLegalPaymentMethod",
      "fleetLegalIsClosingDate",
      "fleetLegalLastStartDate",
      "fleetLegalImportEndDate",
      "fleetLegalComment",
      "fleetLegalRelatedBookingAmount",
      "hullOnLandPolicyNumber",
      "hullOnLandIsPolicyIssued",
      "hullOnLandPolicyDocumentDate",
      "hullOnLandInsurerPartnerNumber",
      "hullOnLandAnnualGrossPremium",
      "bookingAmountHullonLand",
      "hullOnLandProRataGrossPremium",
      "hullOnLandPaymentMethod",
      "hullOnLandIsClosingDate",
      "hullOnLandLastStartDate",
      "hullOnLandImportEndDate",
      "hullOnLandComment",
      "hullOnLandRelatedBookingAmount",
      "machineryBreakdownPolicyNumber",
      "machineryBreakdownIsPolicyIssued",
      "machineryBreakdownPolicyDocumentDate",
      "machineryBreakdownInsurerPartnerNumber",
      "machineryBreakdownAnnualGrossPremium",
      "bookingAmountMachineryBreakdown",
      "machineryBreakdownProRataGrossPremium",
      "machineryBreakdownPaymentMethod",
      "machineryBreakdownIsClosingDate",
      "machineryBreakdownLastStartDate",
      "machineryBreakdownImportEndDate",
      "machineryBreakdownComment",
      "machineryBreakdownRelatedBookingAmount",
      "machineryBreakdownListPrice",
      "machineryBreakdownListPriceTypeCode",
      "machineryBreakdownSpecialEquipment",
      "machineryBreakdownIsIncludingTax",
      "freeTextField1",
      "freeTextField2",
      "hasAttachments",
    ],
    HAS_GREENCARD_EXPIRY_DATE: false,
    HAS_SEARCH_BY_VIN: false,
    HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN: false,
    IS_VEHICLE_ACTIVATION_AVAILABLE: true,
    IS_VEHICLE_SUSPENSION_AVAILABLE: true,
    HAS_REASON_FOR_CONCLUDING_INSURANCE_PREMIUM_FIELD: false,
    HAS_REASON_FOR_CANCELLING_INSURANCE_PREMIUM_FIELD: false,
    HAS_ANNUAL_PREMIUM_EUR: false,
    HAS_HULL_ADDITIONAL_COVER: false,
    HAS_ADDITIONAL_LEASING_FIELDS: false,
    HAS_LOAN_AMOUNT_FIELDS: false,
    HAS_SPECIAL_EQUIPMENT_LIST_FIELD: false,
    HAS_DUPLICATE_WITH_PREMIUMS_OPTION: true,
    HAS_TIMELINE_MENU_OPTIONS: true,
    HAS_CLIENT_COMMENT_EXTERNAL_USER: false,
    HAS_SYNC_FLEET_INSURER_DATA: false,
    INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS: true,

    VISIBLE_FIELDS: ALL_VEHICLE_FIELDS.filter(
      (item) => !["eurotaxCode", "bonusMalusCode"].includes(item),
    ),
    SHOW_BOOKINGS_WITH_DOCUMENT_DATE_CHECKBOX: true,
    HAS_TEMP_VEHICLE_LIST: true,
    HAS_BOOKING_COMPARISON: true,
    SHOW_MAIN_INSURERS: true,
    SHOW_MAIN_INSURERS_LIST: true,
    SHOW_FLEET_EXCEL_UPLOAD: true,
    SHOW_DEFAULT_SETTINGS: true,
    SHOW_RECALCULATE_PRORATA_AND_BOOKINGS: true,
    HAS_MASS_ACTIONS: true,
    PAYMENT_METHODS: ALL_PAYMENT_METHODS.filter(
      (method) => !["PaymentMethod.INDIVIDUAL"].includes(method),
    ),
    SHOW_URGENT_ON_STATUS_CHANGE: false,
    HAS_VEHICLE_HISTORY: true,
    HAS_VEHICLE_ATTACHMENTS: true,
    HAS_FLEET_ATTACHMENTS: true,
    HAS_UPLOAD_VEHICLES: false,
    ALLOW_EXTERNAL_USER_EDIT_VEHICLE_FIELDS: true,
  };

  //.map((column) => defaultColumnsByKey[column])
  private static appSettingsTypes: { [key: string]: AppSettingsType } = {
    "Country.HU": AppSettings.hungarianSettings,
    "Country.CZ": AppSettings.czechSettings,
    "Country.AT": AppSettings.austrianSettings,
    "Country.LT": AppSettings.lithuanianSettings,
  };
}

export default AppSettings;
